import React, { useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Car, Map } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";

const PricingControl = () => {
    const { cabs, packages, adjustAllCabPrices, adjustAllPackagePrices, updateCab, updatePackage } =
        useData();
    const [cabPercent, setCabPercent] = useState(0);
    const [pkgPercent, setPkgPercent] = useState(0);

    const applyCabAdjustment = () => {
        if (cabPercent === 0) return toast.error("Enter a non-zero percentage.");
        if (!window.confirm(`Apply ${cabPercent}% adjustment to ALL cab rates?`)) return;
        adjustAllCabPrices(Number(cabPercent));
        toast.success(`Adjusted all cab rates by ${cabPercent}%.`);
        setCabPercent(0);
    };
    const applyPkgAdjustment = () => {
        if (pkgPercent === 0) return toast.error("Enter a non-zero percentage.");
        if (!window.confirm(`Apply ${pkgPercent}% adjustment to ALL package prices?`)) return;
        adjustAllPackagePrices(Number(pkgPercent));
        toast.success(`Adjusted all package prices by ${pkgPercent}%.`);
        setPkgPercent(0);
    };

    return (
        <div data-testid="admin-pricing-page">
            <h1 className="font-display text-3xl text-ink">Pricing control</h1>
            <p className="text-ink-light text-sm mt-1">Bulk-adjust rates or override individual prices.</p>

            {/* GLOBAL */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h2 className="font-semibold text-ink flex items-center gap-2"><Car className="w-5 h-5 text-saffron-600" /> Cab rates — global adjustment</h2>
                    <p className="text-xs text-ink-light mt-1">Apply percentage change to all cab rates.</p>
                    <div className="flex items-center gap-3 mt-4">
                        <input type="number" value={cabPercent} onChange={(e) => setCabPercent(Number(e.target.value))} className="w-28 px-3 py-2 rounded-lg border border-slate-200 text-center font-semibold" data-testid="cab-percent" />
                        <span className="text-ink-light">%</span>
                        <div className="ml-auto flex gap-2">
                            <button onClick={() => setCabPercent(-10)} className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-red-600 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> -10%</button>
                            <button onClick={() => setCabPercent(10)} className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-teal-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +10%</button>
                        </div>
                    </div>
                    <button onClick={applyCabAdjustment} className="mt-4 w-full px-4 py-2.5 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold transition" data-testid="apply-cab-pricing">
                        Apply to all {cabs.length} cabs
                    </button>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h2 className="font-semibold text-ink flex items-center gap-2"><Map className="w-5 h-5 text-teal-600" /> Package prices — global adjustment</h2>
                    <p className="text-xs text-ink-light mt-1">Apply percentage change to all package prices.</p>
                    <div className="flex items-center gap-3 mt-4">
                        <input type="number" value={pkgPercent} onChange={(e) => setPkgPercent(Number(e.target.value))} className="w-28 px-3 py-2 rounded-lg border border-slate-200 text-center font-semibold" data-testid="pkg-percent" />
                        <span className="text-ink-light">%</span>
                        <div className="ml-auto flex gap-2">
                            <button onClick={() => setPkgPercent(-10)} className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-red-600 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> -10%</button>
                            <button onClick={() => setPkgPercent(10)} className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-teal-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +10%</button>
                        </div>
                    </div>
                    <button onClick={applyPkgAdjustment} className="mt-4 w-full px-4 py-2.5 rounded-lg bg-teal-500 hover:bg-teal-600 text-white font-semibold transition" data-testid="apply-pkg-pricing">
                        Apply to all {packages.length} packages
                    </button>
                </div>
            </div>

            {/* PER-ITEM OVERRIDE */}
            <h2 className="font-display text-2xl text-ink mt-10">Per-item override</h2>
            <p className="text-ink-light text-sm mt-1">Update individual prices inline.</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-ink text-sm">Cabs</div>
                    <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                        {cabs.map((c) => (
                            <div key={c.id} className="flex items-center gap-3 px-5 py-3">
                                <img src={c.image} alt="" className="w-10 h-8 rounded-md object-cover" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-ink text-sm truncate">{c.name}</div>
                                    <div className="text-xs text-ink-light">{c.seats} seats</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-ink-light">₹</span>
                                    <input
                                        type="number"
                                        value={c.ratePerDay}
                                        onChange={(e) => updateCab(c.id, { ratePerDay: Number(e.target.value) })}
                                        className="w-24 px-2 py-1 text-right rounded-md border border-slate-200 text-sm font-semibold text-saffron-600"
                                        data-testid={`override-cab-${c.id}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-ink text-sm">Packages</div>
                    <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                        {packages.map((p) => (
                            <div key={p.id} className="flex items-center gap-3 px-5 py-3">
                                <img src={p.image} alt="" className="w-10 h-8 rounded-md object-cover" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-ink text-sm truncate">{p.route}</div>
                                    <div className="text-xs text-ink-light">{p.days}D · {p.startingCity}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-ink-light">₹</span>
                                    <input
                                        type="number"
                                        value={p.price}
                                        onChange={(e) => updatePackage(p.id, { price: Number(e.target.value) })}
                                        className="w-28 px-2 py-1 text-right rounded-md border border-slate-200 text-sm font-semibold text-saffron-600"
                                        data-testid={`override-pkg-${p.id}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CURRENT VS QUICK STATS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <Stat label="Avg cab rate" value={formatCurrency(Math.round(cabs.reduce((s, c) => s + c.ratePerDay, 0) / Math.max(1, cabs.length)))} />
                <Stat label="Avg package price" value={formatCurrency(Math.round(packages.reduce((s, p) => s + p.price, 0) / Math.max(1, packages.length)))} />
                <Stat label="Cheapest package" value={formatCurrency(Math.min(...packages.map((p) => p.price)))} />
            </div>
        </div>
    );
};

const Stat = ({ label, value }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs uppercase tracking-wider text-ink-light font-semibold">{label}</div>
        <div className="font-display text-2xl font-semibold text-ink mt-1">{value}</div>
    </div>
);

export default PricingControl;
